import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HeaderComponent } from '../../shared/header/header.component';
import { FooterComponent } from '../../shared/footer/footer.component';
import { HealthDeclarationPopupComponent } from '../../components/health-declaration-popup/health-declaration-popup.component';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { LoaderService } from '../../shared/services/loader.service';
import { finalize } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { ActivatedRoute, Router } from '@angular/router';
import { UrlReferenceService } from '../../shared/services/url-reference.service';
import { EmployeeDataService } from '../../shared/services/employee-data.service';
import {
  ApiDependent,
  ApiEmployee,
  ApiStatisticsResponse,
  FilterParams,
  HealthDeclarationResponse,
  Member,
} from '../../models/member.model';
import { CompanyService } from '../../shared/services/company.service';
import { TranslateModule } from '@ngx-translate/core';
// import { InsuranceClassPipe } from '../../pipes/insurance-class.pipe';
import { StepperComponent } from '../../shared/stepper/stepper.component';

@Component({
  selector: 'app-health-declaration',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    HeaderComponent,
    FooterComponent,
    HealthDeclarationPopupComponent,
    HttpClientModule,
    TranslateModule,
    // InsuranceClassPipe,
    StepperComponent,
  ],
  templateUrl: './health-declaration.component.html',
  styleUrls: ['./health-declaration.component.scss'],
})
export class HealthDeclarationComponent implements OnInit {
  downloadAllDeclarations() {
    throw new Error('Method not implemented.');
  }
  healthDeclarationRequired = false;
  termsAccepted = false;
  showPopup = false;

  members: Member[] = [];
  filteredMembers: Member[] = [];
  healthDeclarations: HealthDeclarationResponse[] = [];
  selectedMember: Member = {} as Member;
  selectedMemberType: string = 'All';
  searchQuery: string = '';
  activeMemberListTab = 'All Members';
  selectedQuickFilters = {
    allMembers: true,
    noDeclaration: false,
    needDeclaration: false,
  };

  totalMembers = 0;
  dependentMemberCount = 0;
  employeeMemberCount = 0;
  maleMemberCount = 0;
  femaleMemberCount = 0;
  noDeclarationCount = 0;
  needDeclarationCount = 0;

  currentPage = 0;
  pageSize = 100;
  totalPages = 0;
  totalElements = 0;

  private companyId = localStorage.getItem('companyId') || '';

  constructor(
    private http: HttpClient,
    private loader: LoaderService,
    private toastr: ToastrService,
    private route: ActivatedRoute,
    private router: Router,
    private urlRef: UrlReferenceService,
    private employeeDataService: EmployeeDataService,
    private companyService: CompanyService
  ) {}

  ngOnInit(): void {
    this.companyId =
      localStorage.getItem('companyId') ||
      this.route.snapshot.queryParamMap.get('companyId') ||
      '';
    this.fetchMembers();
    if (this.healthDeclarationRequired) this.fetchHealthDeclarations();
  }

  private fetchMembers(): void {
    this.loader.show();
    this.employeeDataService
      .getMembers(
        this.companyId,
        this.currentPage,
        this.pageSize,
        this.buildFilterParams()
      )
      .pipe(finalize(() => this.loader.hide()))
      .subscribe({
        next: (res) => this.handleMemberResponse(res),
        error: (err) => this.handleError(err),
      });
  }

  private fetchHealthDeclarations(): void {
    this.loader.show();
    this.employeeDataService
      .getHealthDeclarations()
      .pipe(finalize(() => this.loader.hide()))
      .subscribe({
        next: (decs) => {
          this.healthDeclarations = decs;
          this.updateMembersWithDeclarationData();
        },
        error: () => this.toastr.error('Failed to load health declarations.'),
      });
  }

  private handleMemberResponse(res: ApiStatisticsResponse): void {
    const employees = res.content?.employee || [];
    this.members = employees
      .map((emp) => [
        this.toMember(emp),
        ...emp.dependentList.map((dep) => this.toDependent(dep, emp)),
      ])
      .flat();

    this.totalPages = res.totalPages;
    this.totalElements = res.totalElements;
    this.currentPage = res.currentPage;

    this.totalMembers = res.totalMembers;
    this.employeeMemberCount = res.totalEmployees;
    this.dependentMemberCount = res.totalDependents;
    this.noDeclarationCount = res.totalWithNoDeclaration;
    this.needDeclarationCount = res.totalNeedsDeclaration;
    this.maleMemberCount = res.totalMale;
    this.femaleMemberCount = res.totalFemale;

    this.updateMembersWithDeclarationData();
  }

  private toMember(emp: ApiEmployee): Member {
    const fullName =
      emp.firstNameEn + ' ' + (emp.lastNameEn || '') ||
      `Employee (${emp.idNumber})`;
    return {
      id: emp.idNumber,
      idNumber: emp.idNumber,
      name: fullName,
      dob: this.formatDate(emp.dateOfBirth),
      type: 'Employee',
      gender: this.mapGender(emp.gender),
      status: emp.married ? 'Married' : 'Single',
      hasDeclaration: emp.declarationCompleted,
      insuranceClass: emp.insuranceClass || 'N/A',
      declarationAnswersYes: 0,
      dependentCount: emp.dependentList?.length || 0,
    };
  }

  private toDependent(dep: ApiDependent, emp: ApiEmployee): Member {
    const fullName =
      dep.firstNameEn + ' ' + (dep.lastNameEn || '') ||
      `Dependent (${dep.idNumber})`;

    return {
      id: dep.idNumber,
      idNumber: dep.idNumber,
      name: fullName,
      dob: this.formatDate(dep.dateOfBirth),
      type: 'Dependent',
      gender: this.mapGender(dep.gender),
      status: dep.married ? 'Married' : 'Single',
      hasDeclaration: dep.declarationCompleted,
      insuranceClass: dep.insuranceClass || emp.insuranceClass || 'N/A',
      declarationAnswersYes: 0,
      dependentCount: null,
    };
  }

  private updateMembersWithDeclarationData(): void {
    for (const member of this.members) {
      const match = this.healthDeclarations.find(
        (d) =>
          d.idNumber === member.idNumber &&
          ((d.memberType === 'EMPLOYEE' && member.type === 'Employee') ||
            (d.memberType === 'DEPENDENT' && member.type === 'Dependent'))
      );
      member.hasDeclaration = !!match;
      member.declarationAnswersYes = match ? this.countYesAnswers(match) : 0;
    }

    this.filterMembers();
  }

  private countYesAnswers(declaration: HealthDeclarationResponse): number {
    return (
      (declaration.healthDeclaration.hospitalization ? 1 : 0) +
      declaration.healthDeclaration.healthQuestions.length
    );
  }

  private buildFilterParams(): FilterParams {
    const params: FilterParams = {};
    if (this.selectedQuickFilters.needDeclaration)
      params.needsDeclaration = true;
    if (this.selectedQuickFilters.noDeclaration)
      params.noNeedDeclaration = true;
    if (this.selectedMemberType === 'Male') params.male = true;
    if (this.selectedMemberType === 'Female') params.female = true;
    if (this.searchQuery) params.query = this.searchQuery;
    return params;
  }

  private formatDate(dateStr: string): string {
    const [y, m, d] = dateStr.split('-');
    return `${d}/${m}/${y}`;
  }

  private mapGender(gender: string | null | undefined): 'Male' | 'Female' {
    return gender?.toLowerCase() === 'female' ? 'Female' : 'Male';
  }

  private handleError(error: any): void {
    console.error('API Error:', error);
    this.toastr.error('Failed to load members.');
    this.members = [];
  }

  onSearch(): void {
    this.fetchMembers();
  }

  clearFilters(): void {
    this.selectedQuickFilters = {
      allMembers: true,
      noDeclaration: false,
      needDeclaration: false,
    };
    this.selectedMemberType = 'All';
    this.searchQuery = '';
    this.fetchMembers();
  }

  handleQuickFilterChange(
    filter: keyof typeof this.selectedQuickFilters
  ): void {
    const isActive = this.selectedQuickFilters[filter];
    Object.keys(this.selectedQuickFilters).forEach(
      (key) =>
        (this.selectedQuickFilters[
          key as keyof typeof this.selectedQuickFilters
        ] = false)
    );
    this.selectedQuickFilters[isActive ? 'allMembers' : filter] = true;
    this.fetchMembers();
  }

  handleMemberTypeChange(type: string): void {
    this.selectedMemberType = type;
    this.fetchMembers();
  }

  setActiveMemberListTab(tab: string): void {
    this.activeMemberListTab = tab;
    this.fetchMembers();
  }

  filterMembers(): void {
    const query = this.searchQuery.toLowerCase();
    this.filteredMembers = this.members.filter((m) => {
      const matchesSearch =
        m.name.toLowerCase().includes(query) || m.idNumber.includes(query);
      const matchesType =
        this.selectedMemberType === 'All' ||
        m.type === this.selectedMemberType ||
        m.gender === this.selectedMemberType;
      const matchesFilter =
        (this.selectedQuickFilters.noDeclaration && !m.hasDeclaration) ||
        (this.selectedQuickFilters.needDeclaration && m.hasDeclaration) ||
        this.selectedQuickFilters.allMembers;
      return matchesSearch && matchesType && matchesFilter;
    });
  }

  openPopup(member: Member): void {
    this.selectedMember = member;
    this.showPopup = true;
  }

  closePopup(): void {
    this.showPopup = false;
    this.fetchHealthDeclarations();
  }

  onSaveDeclaration(_: any): void {
    const index = this.members.findIndex(
      (m) => m.id === this.selectedMember.id
    );
    if (index > -1) this.members[index].hasDeclaration = true;
    this.filterMembers();
    this.closePopup();
  }

  setHealthDeclarationRequired(value: boolean) {
    this.healthDeclarationRequired = value;
    if (this.healthDeclarationRequired) {
      this.fetchHealthDeclarations();
    }
  }

  canContinue(): boolean {
    return !this.healthDeclarationRequired || this.termsAccepted;
  }

  onBack(): void {
    window.history.back();
  }

  onContinue(): void {
    this.companyService.markStepCompleted(3);
    this.router.navigate(['/quotations'], {
      queryParams: { companyId: this.companyId },
    });
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.fetchMembers();
  }

  get pages(): number[] {
    return Array.from({ length: this.totalPages }, (_, i) => i);
  }

  getCountByKey(key: string): number {
    switch (key) {
      case 'allMembers':
        return this.totalMembers;
      case 'noDeclaration':
        return this.noDeclarationCount;
      case 'needDeclaration':
        return this.needDeclarationCount;
      default:
        return 0;
    }
  }

  getMemberTypeCount(type: string): number {
    switch (type) {
      case 'All':
        return this.totalMembers;
      case 'Employee':
        return this.employeeMemberCount;
      case 'Dependent':
        return this.dependentMemberCount;
      case 'Male':
        return this.maleMemberCount;
      case 'Female':
        return this.femaleMemberCount;
      default:
        return 0;
    }
  }

  getNoDeclarationCount(): number {
    return this.members.filter((m) => !m.hasDeclaration).length;
  }

  getNeedDeclarationCount(): number {
    return this.members.filter(
      (m) => m.hasDeclaration && m.declarationAnswersYes! > 0
    ).length;
  }
}
